import { useState, useMemo, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import SPKCandidatesPositionLayout from '$components/Positions/SPKCandidatesPositionPage/SPKCandidatesPositionLayout/SPKCandidatesPositionLayout';
import PositionInternalDto from '$types/serverTypes/positionInternalDto';
import SPKPositionBar from '$components/Positions/SPKCandidatesPositionPage/SPKPositionBar/SPKPositionBar';

import ActiveValues from '../../../constants/ActiveValues';
import { useLoadOnePositionQuery } from '../../../store/modules/companies';
import { CandidateData } from '../../../types/candidates';
import { filterNames } from '../../../constants/FiltersNames';
import SearchFilter from '../utils/SearchFilter';
import GetCandidateDataByMultipleFetch from '../utils/GetCandidateDataByMultipleFetch';
import ExportShortlistCsv from './ExportShortlistCsv/ExportShortlistCsv';
import { canExportShortlist } from './ExportShortlistCsv/shortlistCsv';

// The employer cards don't show the interview status, so it isn't fetched
const EMPLOYER_HYDRATION_FLAGS = { wantsInterviewStatus: false };

const SPKCandidatesPositionPage = () => {
  const { positionId } = useParams();
  const [candidates, setCandidates] = useState<CandidateData[]>([]);
  const [filteredCandidates, setFilteredCandidates] = useState<CandidateData[]>(
    []
  );
  const [candidatesPositionCount, setCandidatesPositionCount] = useState({
    new: 0,
    shortlisted: 0,
    declined: 0,
    requestInterview: 0,
    hired: 0,
    offerSent: 0,
    all: 0,
  });
  const [searchParams] = useSearchParams();

  const { data: position, isLoading: isLoadingPosition } =
    useLoadOnePositionQuery({
      id: positionId,
    });

  const cps = (searchParams.get('cps') ?? ActiveValues.New) as ActiveValues;

  const filters = useMemo(
    () => ({
      cps,
      matchSkill: searchParams.get(filterNames.skillsMatchs) ?? 'ALL',
      matchAdditionalTags:
        searchParams.get(filterNames.additionalTagsMatch) ?? 'ALL',

      positionId: parseInt(positionId ?? '0', 10),
      id:
        parseInt(searchParams.get(filterNames.id) ?? '0', 10) === 0
          ? null
          : parseInt(searchParams.get(filterNames.id) ?? '0', 10),

      query: searchParams.get(filterNames.query) ?? '',
      name: searchParams.get('name') ?? null,
    }),
    [cps, positionId, searchParams]
  );

  const { data, isLoadingActiveStatus } = GetCandidateDataByMultipleFetch(
    position,
    EMPLOYER_HYDRATION_FLAGS,
    cps
  );

  // Candidates arrive as they load, so the list fills in progressively
  useEffect(() => {
    if (data) {
      setCandidates(data);
    }
  }, [data]);

  useEffect(() => {
    if (position && position.CandidatesPositionStatus) {
      const candidatesCount = position?.CandidatesPositionStatus?.reduce(
        (acc, curr) => {
          acc[curr.Status] += 1;

          if (curr.Status !== 'declined') {
            acc.all += 1;
          }

          return acc;
        },
        {
          new: 0,
          shortlisted: 0,
          declined: 0,
          requestInterview: 0,
          hired: 0,
          offerSent: 0,
          all: 0,
          interested: 0,
        }
      );
      setCandidatesPositionCount(candidatesCount);
    }
  }, [position?.CandidatesPositionStatus, position]);

  const applyFilters = useMemo(() => {
    return SearchFilter(filters, candidates);
  }, [filters, candidates]);

  useEffect(() => {
    setFilteredCandidates(applyFilters);
  }, [applyFilters]);

  return (
    <div className="flex flex-col items-center w-full py-8">
      <SPKPositionBar
        isLoadingPosition={isLoadingPosition}
        candidatesPositionCount={candidatesPositionCount}
        position={position as PositionInternalDto}
        cps={cps}
        showInterestedToEmployer={position?.ShowInterestedToEmployer || false}
      />
      {canExportShortlist(position as PositionInternalDto, cps) && (
        <div className="w-full container px-4 lg:px-0 flex justify-end mt-4">
          <ExportShortlistCsv
            position={position as PositionInternalDto}
            candidates={filteredCandidates}
            disabled={isLoadingActiveStatus}
            viewer="employer"
          />
        </div>
      )}
      <SPKCandidatesPositionLayout
        position={position as PositionInternalDto}
        isLoadingCandidates={
          isLoadingActiveStatus && filteredCandidates.length === 0
        }
        cps={cps}
        candidates={filteredCandidates}
      />
    </div>
  );
};

export default SPKCandidatesPositionPage;
